"use client";
import clsx from "clsx";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

// Context for Tabs state
interface TabsContextType {
  value: string;
  setValue: (val: string) => void;
}
const TabsContext = createContext<TabsContextType | undefined>(undefined);

export const Tabs = ({
  value: controlledValue,
  defaultValue,
  children,
  className,
  onValueChange,
}: {
  value?: string;
  defaultValue?: string;
  children: ReactNode;
  className?: string;
  onValueChange?: (value: string) => void;
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue || "",
  );

  // If controlled, use the value prop; otherwise, use internal state
  const value =
    controlledValue !== undefined ? controlledValue : uncontrolledValue;

  // Keep internal state in sync with defaultValue if not controlled
  useEffect(() => {
    if (controlledValue === undefined && defaultValue !== undefined) {
      setUncontrolledValue(defaultValue);
    }
  }, [defaultValue, controlledValue]);

  const handleSetValue = (val: string) => {
    if (controlledValue === undefined) {
      setUncontrolledValue(val);
    }
    onValueChange?.(val);
  };

  return (
    <TabsContext.Provider value={{ value, setValue: handleSetValue }}>
      <div className={clsx("space-y-2", className)}>{children}</div>
    </TabsContext.Provider>
  );
};

export const TabsList = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={clsx(
      "flex w-full h-12 items-center justify-start bg-white dark:bg-black overflow-x-auto gap-2",
      className,
    )}
  >
    {children}
  </div>
);

export const TabsTrigger = ({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error("TabsTrigger must be used within Tabs");

  const isActive = context.value === value;

  return (
    <button
      onClick={() => context.setValue(value)}
      className={clsx(
        "relative inline-flex items-center justify-center whitespace-nowrap px-5 py-2 text-sm font-semibold transition-colors duration-200 cursor-pointer rounded-t-sm",
        isActive
          ? "text-client-primary bg-primary/10 dark:bg-primary/10"
          : "border-transparent text-gray-500 hover:text-client-primary hover:bg-primary/10 dark:hover:bg-primary/10",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-client-primary focus-visible:ring-offset-2",
        className,
      )}
      tabIndex={0}
      type="button"
      aria-selected={isActive}
      aria-controls={`tab-panel-${value}`}
      role="tab"
    >
      {children}
      {isActive && (
        <span className="absolute left-0 bottom-0 w-full h-0.5 bg-primary/90 rounded-full animate-fade-in" />
      )}
    </button>
  );
};

export const TabsContent = ({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error("TabsContent must be used within Tabs");

  if (context.value !== value) return null;

  return <div className={clsx("", className)}>{children}</div>;
};
